import { groupBy, sortBy } from "lodash"
import { compose } from "lodash/fp"
import { createSelector } from "reselect"
import { addYears, eachDay, isSameDay, isSaturday, isSunday, startOfDay, subDays, isAfter } from 'date-fns'
import { ICreateReservationFormValues } from "../../../containers/Form/CreateReservation/CreateReservation"
import { IEvent } from "../../../models/Event"
import { IRoom } from "../../../models/Room"
import { hasEmptyPricesInCategory } from "../../../utils/Price"
import { byPlaceName, byPlacePersons, withinRange } from "../../../utils/RoomFiltering"
import { areDatesConflicting } from "../../../utils/RoomFiltering/byDate/areEventsConflicting"
import isDateAvailableWithinEvents from "../../../utils/RoomFiltering/byDate/isDateAvailableWithinEvents"
import { IStore } from "../../index"
import { getNewPrices, getPriceConfig, getPrices, getPriceGridFilter, getRoomSettingsGlobal } from "../../price/selectors/price"
import { getBookingObjects } from "../../booking/selectors"
import { getRooms } from "./erhaltenZimmer"
import { getEventsWithLabel, withoutCancelledSortedByStartDate } from "./events"
import { getDateFiltering, getPlaceFiltering } from "./filtering"
import { getUpdateModalReservationId } from "./getModalOpen"
import { dateWithoutOffsert } from "../../../models/License/mappers/mapResponseToLicense"
import { getSelectedColumns } from "./selectedColumns"
import { isWeekend } from "../../../containers/Form/PriceConfiguration/Components/PricesType"
import { getBookingCommissionPercentForPlan } from "../../../models/Price/getBookingCommissionPercentForPlan"
import {
  buildBookingOnlyPlanIds,
  isRoomMappedWithBookingPriceImport,
  shouldComputeBedBookingCommissionPrice,
} from "../../../models/Booking/getNewIndependentBookingPlans"

export function getRoomID(state: IStore, props: { roomId: IRoom['id'] }) {
  return props.roomId
}

export function getStartFinishDates(
  state: IStore,
  props: { dateFrom: Date; dateTo: Date | null },
) {
  return props
}

export function getEditedEventId(
  state: IStore,
  props: { editedEventId: string },
) {
  return props.editedEventId
}

export function getEventData(
  state: IStore,
  props: { eventData: ICreateReservationFormValues },
) {
  return props.eventData
}

export const withoutRemovedRooms = createSelector(
  [getRooms],
  rooms => rooms.filter(r => !r.isDeleted),
)

export const getSortedRoomsByPosition = createSelector(
  [withoutRemovedRooms],
  rooms => sortBy(rooms, ['position']),
)

/**
 * Czy pokój pasuje do wybranych kanałów (room.booking / room.airbnb_mapping).
 * Tylko Airbnb → pokoje z samym Airbnb (bez B.com). Tylko Booking → pokoje z samym B.com (bez Airbnb).
 * Airbnb + Booking → pokoje z Airbnb lub B.com (w tym z oboma).
 */
function roomMatchesChannel(
  room: IRoom,
  channels: { airbnb?: boolean; bedbooking?: boolean; booking?: boolean },
): boolean {
  if (!channels || (!channels.airbnb && !channels.bedbooking && !channels.booking)) return true
  const hasBooking = Boolean(room.booking)
  const hasAirbnb = Boolean(room.airbnb_mapping)
  const isBedBooking = !hasBooking && !hasAirbnb
  const onlyAirbnb = channels.airbnb && !channels.booking && !channels.bedbooking
  const onlyBooking = channels.booking && !channels.airbnb && !channels.bedbooking
  const bothAirbnbAndBooking = channels.airbnb && channels.booking

  if (channels.bedbooking && isBedBooking) return true
  if (onlyAirbnb) return hasAirbnb && !hasBooking
  if (onlyBooking) return hasBooking && !hasAirbnb
  if (bothAirbnbAndBooking) return hasAirbnb || hasBooking
  if (channels.airbnb && hasAirbnb) return true
  if (channels.booking && hasBooking) return true
  return false
}

function roomMatchesCapacity(settings: any, capacities: Record<number, boolean>): boolean {
  if (!capacities || typeof capacities !== 'object') return true
  const checked = Object.entries(capacities).filter(([, v]) => v).map(([k]) => Number(k))
  if (checked.length === 0) return true
  const cap = settings && settings.capacity != null ? Number(settings.capacity) : undefined
  if (cap == null) return false
  return checked.includes(cap)
}

export const getFilteredRoomsForPriceGrid = createSelector(
  [getSortedRoomsByPosition, getRoomSettingsGlobal, getPriceGridFilter],
  (rooms, roomSettingsGlobal, filter) => {
    if (!filter || (!filter.channels && !filter.capacities)) return rooms
    const hasChannelFilter =
      filter.channels &&
      (filter.channels.airbnb || filter.channels.bedbooking || filter.channels.booking)
    const hasCapacityFilter = filter.capacities && Object.values(filter.capacities).some(Boolean)
    if (!hasChannelFilter && !hasCapacityFilter) return rooms
    const globalList = Array.isArray(roomSettingsGlobal) ? roomSettingsGlobal : []
    return rooms.filter(room => {
      const roomId = String(room.id)
      const settings = globalList.find(
        (r: any) => String(r?.id) === roomId || String(r?.id_room) === roomId,
      )
      if (hasChannelFilter && !roomMatchesChannel(room, filter.channels)) return false
      if (hasCapacityFilter && !roomMatchesCapacity(settings, filter.capacities)) return false
      return true
    })
  },
)

export const findRoomById = createSelector(
  [withoutRemovedRooms, getRoomID],
  (rooms, id) => rooms.find(room => room.id === id),
)

export const findRoomForOpenModal = id =>
  createSelector(
    [getRooms],
    rooms => rooms.find(room => room.id === id),
  )

export const getRoomsPositions = createSelector(
  [withoutRemovedRooms],
  rooms => rooms.map(room => room.position),
)

export const getNextId = id =>
  createSelector(
    [getSortedRoomsByPosition, findRoomForOpenModal(id)],
    (rooms, r) => {
      const index = rooms.indexOf(rooms.find(room => room.id === r.id))
      if (index >= 0 && index < rooms.length - 1) {
        return rooms[index + 1].id
      } else {
        return rooms[0].id
      }
    },
  )

export const getPrevId = id =>
  createSelector(
    [getSortedRoomsByPosition, findRoomForOpenModal(id)],
    (rooms, r) => {
      const index = rooms.indexOf(rooms.find(room => room.id === r.id))
      if (index > 0 && index < rooms.length) {
        return rooms[index - 1].id
      } else {
        return rooms[rooms.length - 1].id
      }
    },
  )

export const getRoomsWithEvents = createSelector(
  [getEventsWithLabel, getSortedRoomsByPosition],
  (events, rooms) => {
    return rooms.map(
      (room): IRoomWithEvents => ({
        ...room,
        events: events.filter(event => event.roomId === room.id),
      }),
    )
  },
)

export const getFilteredRoomsWithEvents = createSelector(
  [getRoomsWithEvents, getPlaceFiltering, getDateFiltering],
  (rooms, byPlace, byDate): IRoomWithEvents[] => {
    return compose<any, any, any, any>(
      byPlacePersons(byPlace),
      byPlaceName(byPlace),
      withinRange(byDate),
    )(rooms)
  },
)

export function getFilteredEventsByDates(startDate: Date, endDate: Date) {
  return createSelector(
    [getFilteredRoomsWithEvents],
    rooms => {
      return rooms
    },
  )
}

export const getRoomsWithEventsWithoutLabel = createSelector(
  [getSortedRoomsByPosition, withoutCancelledSortedByStartDate],
  (rooms, events) =>
    rooms.map(
      (room): IRoomWithEvents => ({
        ...room,
        events: events.filter(event => event.roomId === room.id),
      }),
    ),
)

export const findRoomByIdWithEvents = createSelector(
  [getRoomsWithEvents, getRoomID],
  (rooms, roomId): IRoomWithEvents => rooms.find(r => r.id === roomId),
)

export const getFilteredRoomsOnlyColors = createSelector(
  [getFilteredRoomsWithEvents],
  rooms =>
    rooms.map(r => (r.customColor ? r.customColor.rgb().toString() : null)),
)

export const getRoomsOnlyColors = createSelector(
  [getSortedRoomsByPosition],
  rooms =>
    rooms.map(r => (r.customColor ? r.customColor.rgb().toString() : null)),
)

export const getRoomsAvailableWithinDateRange = createSelector(
  [getRoomsWithEventsWithoutLabel, getStartFinishDates],
  (rooms, dates) => {
    return rooms.filter(r =>
      isDateAvailableWithinEvents(r.events, dates.dateFrom, dates.dateTo),
    )
  },
)

export const getRoomsAvailableWithinDateRangeWithoutUpdatedEvent = createSelector(
  [
    getRoomsWithEventsWithoutLabel,
    getUpdateModalReservationId,
    getStartFinishDates,
  ],
  (rooms, editedEventId, dates) => {
    return rooms.filter(r => {
      return isDateAvailableWithinEvents(
        r.events,
        dates.dateFrom,
        dates.dateTo,
        [editedEventId],
      )
    })
  },
)

export const getRoomsAvailableWithinDateRangeWithoutEvent = createSelector(
  [getRoomsWithEventsWithoutLabel, getStartFinishDates, getEditedEventId],
  (rooms, dates, editedEventId) => {
    return rooms.filter(r => {
      return isDateAvailableWithinEvents(
        r.events,
        dates.dateFrom,
        dates.dateTo,
        [editedEventId],
      )
    })
  },
)

export const getRoomsAvailableWithinDateRangeWithoutFormEvent = createSelector(
  [getRoomsAvailableWithinDateRange, getStartFinishDates, getEventData],
  (rooms, dates, eventData) => {
    if (!eventData.roomId) {
      return rooms
    }
    return rooms.filter(
      r =>
        eventData.roomId !== r.id ||
        !areDatesConflicting(
          dates.dateFrom,
          dates.dateTo,
          eventData.dateFrom,
          eventData.dateTo,
        ),
    )
  },
)
const today = new Date()
today.setDate(today.getDate() - 30)
export const getUnavailableDatesForRoomWithoutEvent = createSelector(
  [findRoomByIdWithEvents, getUpdateModalReservationId],
  (room, eventId) => {
    const events = [
      ...(room
        ? room.events.filter(e => e.id !== eventId && e.finishDate > today)
        : []),
    ]
    let acc = []
    for (let i = 0; i < events.length; i++) {
      const event = { ...events[i] }
      const startDate = event.startDate
      const endDate = subDays(event.finishDate, 1)

      if (isAfter(startDate, endDate)) continue
      const dates = eachDay(startDate, endDate)
      acc = acc.concat(dates)
    }

    return acc
  },
)

// export const getUnavailableDatesForAllRooms = createSelector(
//   [getRoomsWithEvents],
//   rooms => {
//     const datesMap = rooms.map(room =>
//       room.events.reduce(
//         (acc, event) =>
//           acc.concat(eachDay(event.startDate, subDays(event.finishDate, 1))),
//         [] as Date[],
//       ),
//     )
//
//     const comparator = (dateLeft: Date, dateRight: Date) =>
//       isSameDate(dateLeft, dateRight)
//
//     return datesMap.length
//       ? datesMap.slice(1).reduce((acc, dates) => {
//           return intersectionWith(acc, dates, comparator)
//         }, datesMap[0])
//       : []
//   },
// )

export const getRoomsWithPrices = createSelector(
  [getFilteredRoomsWithEvents, getPrices],
  (rooms, prices) =>
    rooms.map(r => {
      const roomPrice = prices
        ? prices.find(price => String(price.roomId) === String(r.id))
        : null

      if (!roomPrice) {
        return {
          ...r,
          prices: {
            prices: [],
            restrictions: [],
          },
        }
      }

      const normalizedPrices = (roomPrice.prices.length === undefined
        ? [roomPrice.prices]
        : roomPrice.prices) as any

      const mappedPrice = {
        ...roomPrice,
        prices: normalizedPrices,
      }

      return {
        ...r,
        prices: mappedPrice,
      }
    }),
)

export const getRoomsWithNewPrices = createSelector(
  [getFilteredRoomsWithEvents, getNewPrices],
  (rooms, prices) => {
    const roomMapped = rooms.map(r => {
      const roomPrice = prices
        ? prices.find(price => String(price.id_room) === String(r.id))
        : null

      if (!roomPrice) {
        return {
          ...r,
          prices: {
            prices: [],
            airbnb: [],
            booking: [],
            restrictions: [],
          },
        }
      }

      const normalizedPrices = (roomPrice.prices.bedbooking.length === undefined
        ? [roomPrice.prices.bedbooking]
        : roomPrice.prices.bedbooking) as any
      const normalizedPricesBooking = (roomPrice.prices.booking.length === undefined
        ? [roomPrice.prices.booking]
        : roomPrice.prices.booking) as any
      const normalizedPricesAirbnb = (roomPrice.prices.airbnb.length === undefined
        ? [roomPrice.prices.airbnb]
        : roomPrice.prices.airbnb) as any

      const mappedPrice = {
        ...roomPrice,
        prices: normalizedPrices,
      }
      const mappedPriceBooking = {
        ...roomPrice,
        prices: normalizedPricesBooking,
        restrictions: undefined
      }
      const mappedPriceAirbnb = {
        ...roomPrice,
        prices: normalizedPricesAirbnb,
        restrictions: undefined
      }

      return {
        ...r,
        prices: mappedPrice,
        booking: mappedPriceBooking,
        airbnb: mappedPriceAirbnb,
      }
    })

    return roomMapped

  })

export const getRoomsWithPricesAndWarning = createSelector(
  [getRoomsWithPrices, getPriceConfig],
  (rooms, config) => {
    return rooms.map(room => ({
      ...room,
      prices: {
        ...room.prices,
        prices: room.prices.prices.map(p => ({
          ...p,
          warning: hasEmptyPricesInCategory(p, room, config),
        })),
      },
    }))
  },
)

export const getRoomsWithNewPricesAndWarning = createSelector(
  [getRoomsWithNewPrices, getPriceConfig],
  (rooms, config) => {

    const mappedRooms = rooms.map(room => ({
      ...room,
      prices: {
        ...room.prices,
        prices: room.prices.prices.map(p => ({
          ...p,
          warning: hasEmptyPricesInCategory(p, room, config),
        })),
      },
    }))
    return mappedRooms
  },
)

export const getRoomsNewPricesSelectedDays = createSelector(
  [getSelectedColumns, getRoomsWithNewPricesAndWarning],
  (selectedColumns, rooms) => {
    const selectedDays = {}
    const minDate = startOfDay(dateWithoutOffsert(new Date()))
    const maxDate = addYears(startOfDay(dateWithoutOffsert(new Date())), 2)
    const daysBetween = eachDay(minDate, maxDate)
    daysBetween.forEach((d,index) => {
      if (selectedColumns.some(c => isSameDay(c, d))) {
        selectedDays[index] = true
      } else {
        selectedDays[index] = false
      }
    })
    return selectedDays
  }
)

const minDate = startOfDay(dateWithoutOffsert(new Date()))
const maxDate = addYears(startOfDay(dateWithoutOffsert(new Date())), 2)
const daysBetween = eachDay(minDate, maxDate).map(date => {
  return date.getTime() - date.getTimezoneOffset() * 60 * 1000
});


export const precomputedPricesCache = new Map();
export const precomputedRestrictionsCache = new Map();

/** Po zapisie / kopiowaniu cen — siatka musi przebudować mapowanie Booking (cache ignoruje nowe dane z API). */
export function clearPrecomputedPriceCachesForRooms(
  roomIds: Array<string | number>,
) {
  roomIds.forEach(id => {
    const key = String(id)
    precomputedPricesCache.delete(key)
    precomputedPricesCache.delete(`pricesMapped-${key}`)
    precomputedRestrictionsCache.delete(key)
    precomputedRestrictionsCache.delete(`restrictionsMapped-${key}`)
    const numericKey = Number(id)
    if (!Number.isNaN(numericKey)) {
      precomputedPricesCache.delete(numericKey)
      precomputedPricesCache.delete(`pricesMapped-${numericKey}`)
      precomputedRestrictionsCache.delete(numericKey)
      precomputedRestrictionsCache.delete(`restrictionsMapped-${numericKey}`)
    }
  })
}

export function clearAllPrecomputedPriceCaches() {
  precomputedPricesCache.clear()
  precomputedRestrictionsCache.clear()
}

const toIso = (d) => new Date(d).toISOString().substring(0, 10)

function getPrecomputedPricesWithDates(roomId, prices) {
  if (precomputedPricesCache.has(roomId)) {
    return precomputedPricesCache.get(roomId);
  } else {
    const pricesWithDates = prices.map(price => ({
      ...price,
      startDate: toIso(price.price_day_from),
      finishDate: toIso(price.price_day_to),
    }));
    precomputedPricesCache.set(roomId, pricesWithDates);
    return pricesWithDates;
  }
}

function getPrecomputedRestrictionsWithDates(roomId, restrictions) {
  if (precomputedRestrictionsCache.has(roomId)) {
    return precomputedRestrictionsCache.get(roomId);
  } else {
    const restrictionsWithDates = restrictions.map(restriction => ({
      ...restriction,
      startDate: toIso(restriction.restriction_day_from),
      finishDate: toIso(restriction.restriction_day_to),
    }));
    precomputedRestrictionsCache.set(roomId, restrictionsWithDates);
    return restrictionsWithDates;
  }
}

function getPricesMappedForRoom(roomId, pricesWithDates) {
  if (precomputedPricesCache.has(`pricesMapped-${roomId}`)) {
    return precomputedPricesCache.get(`pricesMapped-${roomId}`);
  } else {
    const pricesMapped = {};
    const groupedPrices = groupBy(pricesWithDates, 'external_pricing_id');

    Object.entries(groupedPrices).forEach(([key, value]) => {
      daysBetween.forEach((dayTimestamp, index) => {
        const prices = value.find(
          ({ startDate, finishDate }) => toIso(startDate) <= toIso(dayTimestamp) && toIso(finishDate) >= toIso(dayTimestamp)
        );

        if (prices) {
          pricesMapped[key] = pricesMapped[key] || {};
          pricesMapped[key][index] = pricesMapped[key][index] || [];
          pricesMapped[key][index].push(prices);
        }
      });
    });

    precomputedPricesCache.set(`pricesMapped-${roomId}`, pricesMapped);
    return pricesMapped;
  }
}

function getRestrictionsMappedForRoom(roomId, restrictionsWithDates) {
  if (precomputedRestrictionsCache.has(`restrictionsMapped-${roomId}`)) {
    return precomputedRestrictionsCache.get(`restrictionsMapped-${roomId}`);
  } else {

    const restrictionsMapped = {};
    const groupedRestrictions = groupBy(restrictionsWithDates, 'external_pricing_id');

    Object.entries(groupedRestrictions).forEach(([key, value]) => {
      daysBetween.forEach((dayTimestamp, index) => {
        const restriction = value.find(
          ({ startDate, finishDate }) => toIso(startDate) <= toIso(dayTimestamp) && toIso(finishDate) >= toIso(dayTimestamp)
        );

        if (restriction) {
          restrictionsMapped[key] = restrictionsMapped[key] || {};
          restrictionsMapped[key][index] = restrictionsMapped[key][index] || [];
          restrictionsMapped[key][index].push(restriction);
        }
      });
    });

    precomputedRestrictionsCache.set(`restrictionsMapped-${roomId}`, restrictionsMapped);
    return restrictionsMapped;
  }
}


/** Booking: price_change_type '0' = procent, '1' = kwota stała */
function applyDependentPriceChange(parentVal, amount, changeType) {
  if (parentVal == null || parentVal === '' || Number.isNaN(Number(parentVal))) return parentVal
  const p = Number(parentVal)
  if (p === 0) return 0
  const a = Number(amount) || 0
  if (changeType === '0' || changeType === 0) {
    return Math.round(p * (1 + a / 100) * 100) / 100
  }
  return Math.round((p + a) * 100) / 100
}

export const getRoomsWithNewPricesAndMappedEvents = createSelector(
  [getRoomsWithNewPricesAndWarning, getPriceConfig, getRoomSettingsGlobal, getBookingObjects],
  (rooms, config, global, bookingObjects) => {
    const roomsO = {}
    const bookingIntegration = config.integrations?.booking
    const bookingOnlyPlanIds = buildBookingOnlyPlanIds(
      bookingObjects,
      bookingIntegration,
    )
    //console.log(minDate, daysBetween)

    const mapRestrictions = (restr) => {
      if(!config.restrictions || !restr) return false
      if(config.restrictionCloseAtDeparture && !!restr.restriction_close_at_departure) return true
      if(config.restrictionCloseAtArrival && !!restr.restriction_close_at_arrival) return true
      if(config.restrictionExactStay && !!restr.restriction_exact_stay) return true
      if(config.restrictionMaxStay && !!restr.restriction_max_stay) return true
      if(config.restrictionMinStay && !!restr.restriction_min_stay) return true
      if(config.restrictionMinThru && !!restr.restriction_min_stay_thru) return true
      if(config.restrictionMaxStayThru && !!restr.restriction_max_stay_thru) return true
      if(config.restrictionMinBeforeArrival && !!restr.restriction_min_before_arrival) return true
      if(config.restrictionMaxBeforeArrival && !!restr.restriction_max_before_arrival) return true

      return false
    }

    const mapPricing = (pricing, config, d, r, standardWeekend = false, airbnb = false) => {
      let roomSize = Number(r.roomSize)
      if(airbnb) {
        const int = global && global.find(ro => String(ro.id) === r.id )? global.find(ro => String(ro.id) === r.id ).integrations:{}
        if(int) {
          if(int.airbnb) {
            roomSize = int.airbnb.person_capacity? Number(int.airbnb.person_capacity): Number(r.roomSize)
          }
        }
      }
      let prices = undefined

      if(pricing) {
        if(config.weekendPrice && isWeekend(d) && pricing.weekend) {
          const pWeekend = pricing.weekend[roomSize]
          if(pWeekend && pWeekend !== '0.00'){
            prices = formatNumber(pWeekend)
          } else {
            if(standardWeekend) {
              const pStandard = pricing.standard[roomSize]
              if(pStandard != null && pStandard !== ''){
                prices = formatNumber(pStandard)
              }
            } else {
              prices = undefined
            }
          }
        } else if(pricing.standard && !(config.weekendPrice && isWeekend(d))){
          const pStandard = pricing.standard[roomSize]
          if(pStandard != null && pStandard !== ''){
            prices = formatNumber(pStandard)
          } else {
            prices = undefined
          }
        } else {
          prices = undefined
        }


      } else {
        prices = undefined
      }
      return prices
    }

    const mapErrors = (pricing, r, d, bedbooking = false, airbnb = false) => {
      let errors = false
      let roomSize = Number(r.roomSize)
      let int = undefined
      if(airbnb) {
        const integration = global && global.find(ro => String(ro.id) === r.id )? global.find(ro => String(ro.id) === r.id ).integrations:{}
        if(integration) {
          int=integration
          if(integration.airbnb) {
            roomSize = integration.airbnb.person_capacity? Number(integration.airbnb.person_capacity): Number(r.roomSize)
          }
        }
      }

      if (config.pricePerPerson && ((airbnb && int)? int.airbnb && Boolean(int.airbnb.additional_price_per_person) && Boolean(int.airbnb.additional_after_persons) :true)) {
        const weekendPrice = pricing.weekend
        if (config.weekendPrice && Object.values(weekendPrice).some(v => v === null || v === undefined) && isWeekend(d)) {
          for (let i = 1; i <= Number(roomSize); i++) {
            if (!weekendPrice[i]) {
              errors = true
              return errors
            } else {
              errors = false
            }
          }
        } else {
          const standardPrice = pricing.standard
          if(standardPrice && Object.values(standardPrice).some(v => v !== null)) {
            for (let i = 1; i <= Number(roomSize); i++) {
              if (!standardPrice[i]) {
                errors = true
                return errors
              } else {
                errors = false
              }
            }
          }
        }


        if(bedbooking && Boolean(config.stayLongFrom)) {
          const stayLongFrom = pricing.long
          if(stayLongFrom) {
            for (let i = 1; i <= Number(r.roomSize); i++) {
              if (!stayLongFrom[i]) {
                errors = true
                return errors
              }
            }
          }
        }

        if(bedbooking && Boolean(config.stayShortTo)) {
          const stayShortTo = pricing.short
          if(stayShortTo) {
            for (let i = 1; i <= Number(r.roomSize); i++) {
              if (!stayShortTo[i]) {
                errors = true
                return errors
              }
            }
          }
        }

      } else {
        if(config.weekendPrice && isWeekend(d) && (pricing.weekend[roomSize] === undefined || pricing.weekend[roomSize] === null )) {
          errors = true
          return errors
        }
        if(bedbooking) {
          if((config.weekendPrice && isWeekend(d) && pricing.weekend[roomSize] !== null) || (pricing.standard[roomSize] !==null)) {
            if(Boolean(config.stayShortTo) && pricing.short[roomSize] === null) {
              errors = true
              return errors
            }
            if(Boolean(config.stayLongFrom) && pricing.long[r.roomSize] === null) {
              errors = true
              return errors
            }
            if(config.kidsPrice){
              if(!pricing.kids || pricing.kids.length === 0) {
                errors = true
                return errors
              }
              if(pricing.kids && pricing.kids.length !== config.kids.length){
                errors = true
                return errors
              }
              if(pricing.kids){
                const errors = pricing.kids.some(k => {
                  if(config.weekendPrice && isWeekend(d) && !k.weekend){
                    return true
                  } else if(!k.standard){
                    return true
                  }
                  if(Boolean(config.stayLongFrom) && !k.long) {
                    return true
                  }
                  if(Boolean(config.stayShortTo) && !k.short) {
                    return true
                  }
                  return false
                })
                if(errors){
                  return errors
                }
              }
            }
          }
        }
      }


      if(bedbooking && pricing.extrabed && Object.keys(pricing.extrabed).length > 0) {
        const extrabed = pricing.extrabed
        if(bedbooking && Boolean(config.stayShortTo)) {
          if(extrabed.short === null) {
            errors = true
            return errors
          }
        }
        if(bedbooking && Boolean(config.stayLongFrom)) {
          if(extrabed.long === null) {
            errors = true
            return errors
          }
        }
        if(config.weekendPrice  && isWeekend(d)){
          if(extrabed.weekend === null) {
            errors = true
            return errors
          }
        } else {
          if(extrabed.standard === null) {
            errors = true
            return errors
          }
        }
      }


      return errors
    }






    const roomsGrouped = rooms.map(r => {
      const pricesWithPrecomputedDates = getPrecomputedPricesWithDates(r.id, r.booking.prices);
      const restrictionsWithPrecomputedDates = getPrecomputedRestrictionsWithDates(r.id, r.prices.restrictions.booking);

      const pricesMapped = getPricesMappedForRoom(r.id, pricesWithPrecomputedDates);
      const restrictionsMapped = getRestrictionsMappedForRoom(r.id, restrictionsWithPrecomputedDates);
      //if(r.id === "892275") {
      //  console.log(pricesMapped, pricesWithPrecomputedDates, r)
      //}
      return {
        ...r,
        booking: {
          ...r.booking,
          prices: pricesMapped,
        },
        prices: {
          ...r.prices,
          restrictions: {
            ...r.prices.restrictions,
            booking: restrictionsMapped,
          },
        },
      };
    });

    //console.log(roomsGrouped)


    const allMappings = (bookingObjects || []).reduce((acc, obj) => acc.concat(obj.mappings || []), [])
    const allRooms = (bookingObjects || []).reduce((acc, obj) => acc.concat(obj.rooms || []), [])

    const roomsMapped = roomsGrouped.map(r => {
      const mapping = allMappings.find(m => (m.bed_booking_rooms || []).includes(String(r.id)))
      const bookingRoom = mapping ? allRooms.find(room => String(room.id) === String(mapping.booking_id)) : null
      const roomSettingsForR = global && global.find(ro => String(ro.id) === r.id)
      const bookingIntegration = config.integrations?.booking
      const globalBookingActive = Boolean(Number(bookingIntegration?.active))
      const roomUsesBookingPriceImport = isRoomMappedWithBookingPriceImport(
        roomSettingsForR?.integrations?.booking,
        globalBookingActive,
      )

      let pricesD = {}
      let pricesB = {}
      let pricesA = {}
      let restr = {}
      let restrB = {}
      let restrA = {}



      let errors = {}
      let errorsA = {}
      let errorsB = {}
      let weekdays = {}

      const precomputetPricesDates = r.prices.prices.map(price => ({
        ...price,
        startDate: toIso(price.price_day_from),
        finishDate: toIso(price.price_day_to),
      }));

      const precomputetRestrictionsDates = r.prices.restrictions.bedbooking.map(restriction => ({
        ...restriction,
        startDate: toIso(restriction.restriction_day_from),
        finishDate: toIso(restriction.restriction_day_to),
      }));

      const precomputetRestrictionsDatesA = r.prices.restrictions.airbnb.map(restriction => ({
        ...restriction,
        startDate: toIso(restriction.restriction_day_from),
        finishDate: toIso(restriction.restriction_day_to),
      }));

      //if(r.id === "892275") {
      //  console.log(precomputetPricesDates)
      //}

      const precomputedPricesDatesA = r.airbnb.prices.map(price => ({
        ...price,
        startDate: toIso(price.price_day_from),
        finishDate: toIso(price.price_day_to),
      }));


      const eventsWithTimestamps = r.events.map(e => ({
        ...e,
        eventStartDate: startOfDay(e.startDate).getTime(),
        eventFinishDate: startOfDay(e.finishDate).getTime(),
      }));

      const dayIndexMap = {};
      daysBetween.forEach((d, index) => {
        dayIndexMap[d] = index;
      });

      const mappedEvents = new Array(daysBetween.length).fill(0);
      const dayInMilliseconds = 24 * 60 * 60 * 1000;
      const minDate = daysBetween[0];

      eventsWithTimestamps.forEach(e => {
        const startIndex = Math.max(0, Math.round((e.eventStartDate - minDate) / dayInMilliseconds));
        const endIndex = Math.min(daysBetween.length - 1, Math.round((e.eventFinishDate - minDate) / dayInMilliseconds));


        for (let index = startIndex; index <= endIndex; index++) {
          if (mappedEvents[index] === 0) {
            if (startIndex === endIndex) {
              mappedEvents[index] = 5; // Event starts and ends on the same day
            } else if (index === startIndex) {
              mappedEvents[index] = 1; // Event starts on this day
            } else if (index === endIndex) {
              mappedEvents[index] = 2; // Event ends on this day
            } else {
              mappedEvents[index] = 3; // Event continues on this day
            }
          } else {
            mappedEvents[index] = 4; // Multiple events on this day
          }
        }
      });


      daysBetween.forEach((d,index) => {

        weekdays[index] = isSaturday(d) || isSunday(d)

        const pricing = precomputetPricesDates.find(({ startDate, finishDate }) => startDate <= toIso(d) && finishDate >= toIso(d))

        //if(r.id === "892275") {
        //  console.log(d,toIso(d), index, pricing)
        //}

        const restricitons = precomputetRestrictionsDates.find(({ startDate, finishDate }) => startDate <= toIso(d) && finishDate >= toIso(d))

        const restricitonsA = precomputetRestrictionsDatesA.find(({ startDate, finishDate }) => startDate <= toIso(d) && finishDate >= toIso(d))
        const restricitonsB:any = Object.values(r.prices.restrictions.booking).map(v => v[index]).reduce((acc, val) => acc.concat(val), []).filter(Boolean)

        restr[index] = mapRestrictions(restricitons);
        restrA[index] = mapRestrictions(restricitonsA);



        const pricingB:any = Object.values(r.booking.prices).map(v => v[index]).reduce((acc, val) => acc.concat(val), []).filter(Boolean)

        const pricingA = precomputedPricesDatesA.find(({ startDate, finishDate }) => startDate <= toIso(d) && finishDate >= toIso(d))

        pricesD[index] = mapPricing(pricing, config, d, r, true)
        pricesA[index] = mapPricing(pricingA, config, d, r, true, true)

        if(pricingB && pricingB.length) {
          pricingB.forEach(p => {
            if(!pricesB[p.external_pricing_id]){
              pricesB[p.external_pricing_id] = {}
            }
            const dependentPlan = bookingRoom && bookingRoom.pricing && bookingRoom.pricing.find(pl => String(pl.booking_id) === String(p.external_pricing_id) && Number(pl.is_child) === 1)
            if (dependentPlan) {
              const parentRecord = pricingB.find(pp => String(pp.external_pricing_id) === String(dependentPlan.id_parent))
              if (parentRecord) {
                const parentVal = mapPricing(parentRecord, config, d, r, true)
                const savedDep = roomSettingsForR?.integrations?.booking?.find(b => String(b.booking_id_pricing) === String(dependentPlan.booking_id))
                const amount = savedDep?.price_change_amount !== undefined && savedDep?.price_change_amount !== null
                  ? savedDep.price_change_amount
                  : (dependentPlan.price_change_amount ?? dependentPlan.price_change ?? 0)
                const changeType = savedDep?.price_change_type !== undefined && savedDep?.price_change_type !== null
                  ? savedDep.price_change_type
                  : (dependentPlan.price_change_type ?? '1')
                const computed = applyDependentPriceChange(parentVal, amount, changeType)
                pricesB[p.external_pricing_id][index] = computed != null ? formatNumber(computed) : mapPricing(p, config, d, r, true)
              } else {
                pricesB[p.external_pricing_id][index] = mapPricing(p, config, d, r, true)
              }
            } else {
              pricesB[p.external_pricing_id][index] = mapPricing(p, config, d, r, true)
            }
          })
          // Uzupełnij ceny dla planów zależnych, które nie mają rekordu w API w tym dniu – oblicz z planu głównego
          const dependentPlans = bookingRoom && bookingRoom.pricing ? bookingRoom.pricing.filter(pl => Number(pl.is_child) === 1) : []
          dependentPlans.forEach(dep => {
            if (pricingB.some(p => String(p.external_pricing_id) === String(dep.booking_id))) return
            const parentRecord = pricingB.find(pp => String(pp.external_pricing_id) === String(dep.id_parent))
            if (!parentRecord) return
            const parentVal = mapPricing(parentRecord, config, d, r, true)
            if (parentVal == null) return
            const savedDep = roomSettingsForR?.integrations?.booking?.find(b => String(b.booking_id_pricing) === String(dep.booking_id))
            const amount = savedDep?.price_change_amount !== undefined && savedDep?.price_change_amount !== null
              ? savedDep.price_change_amount
              : (dep.price_change_amount ?? dep.price_change ?? 0)
            const changeType = savedDep?.price_change_type !== undefined && savedDep?.price_change_type !== null
              ? savedDep.price_change_type
              : (dep.price_change_type ?? '1')
            const computed = applyDependentPriceChange(parentVal, amount, changeType)
            if (computed != null) {
              if (!pricesB[dep.booking_id]) pricesB[dep.booking_id] = {}
              pricesB[dep.booking_id][index] = formatNumber(computed)
            }
          })
        }

        if (
          bookingRoom?.pricing &&
          pricesD[index] !== undefined &&
          !roomUsesBookingPriceImport
        ) {
          const bbPrice = pricesD[index]
          bookingRoom.pricing
            .filter(pl => !pl.deleted && Number(pl.is_child) !== 1)
            .forEach(plan => {
              const planId = String(plan.booking_id)
              const integration = roomSettingsForR?.integrations?.booking?.find(
                b => String(b.booking_id_pricing) === planId,
              )
              if (
                !shouldComputeBedBookingCommissionPrice(
                  planId,
                  bookingIntegration,
                  globalBookingActive,
                  roomUsesBookingPriceImport,
                  integration,
                  bookingOnlyPlanIds,
                ) ||
                pricesB[planId]?.[index] !== undefined
              ) {
                return
              }
              if (!pricesB[planId]) pricesB[planId] = {}
              if (bbPrice === 0 || bbPrice === '0') {
                pricesB[planId][index] = 0
              } else {
                const pct = getBookingCommissionPercentForPlan(
                  config.integrations.booking,
                  planId,
                  integration,
                )
                const p = Number(bbPrice) * (1 + pct / 100)
                pricesB[planId][index] =
                  p % 1 < 0.01 ? Math.round(p) : Math.ceil(p)
              }
            })
          bookingRoom.pricing
            .filter(pl => !pl.deleted && Number(pl.is_child) === 1)
            .forEach(dep => {
              const depId = String(dep.booking_id)
              const savedDep = roomSettingsForR?.integrations?.booking?.find(
                b => String(b.booking_id_pricing) === String(dep.booking_id),
              )
              if (
                !shouldComputeBedBookingCommissionPrice(
                  depId,
                  bookingIntegration,
                  globalBookingActive,
                  roomUsesBookingPriceImport,
                  savedDep,
                  bookingOnlyPlanIds,
                ) ||
                pricesB[depId]?.[index] !== undefined
              ) {
                return
              }
              const parentId = String(dep.id_parent)
              const parentVal = pricesB[parentId]?.[index]
              if (parentVal === undefined) return
              const amount =
                savedDep?.price_change_amount !== undefined &&
                savedDep?.price_change_amount !== null
                  ? savedDep.price_change_amount
                  : dep.price_change_amount ?? dep.price_change ?? 0
              const changeType =
                savedDep?.price_change_type !== undefined &&
                savedDep?.price_change_type !== null
                  ? savedDep.price_change_type
                  : dep.price_change_type ?? '1'
              const computed = applyDependentPriceChange(
                parentVal,
                amount,
                changeType,
              )
              if (computed != null) {
                if (!pricesB[depId]) pricesB[depId] = {}
                pricesB[depId][index] = formatNumber(computed)
              }
            })
        }

        if(restricitonsB) {
          restricitonsB.forEach(re => {
            if(!restrB[re.external_pricing_id]){
              restrB[re.external_pricing_id] = {}
            }
            restrB[re.external_pricing_id][index] = mapRestrictions(re)
          })
        }

        if(pricing) {
          errors[index] = mapErrors(pricing, r, d, true)
        }

        if(pricingA) {
          errorsA[index] = mapErrors(pricingA, r, d, false, true)
        }

        if(pricingB) {
          pricingB.forEach(p => {
            if(!errorsB[p.external_pricing_id]){
              errorsB[p.external_pricing_id] = {}
            }
            errorsB[p.external_pricing_id] = mapErrors(p, r, d)
          })
        }
      })

      return {
        ...r,
        mappedEvents: mappedEvents,
        mappedPrices: pricesD,
        mappedPricesBooking: pricesB,
        mappedPricesAirbnb: pricesA,
        errors,
        errorsA,
        errorsB,
        integrations: global && global.find(ro => String(ro.id) === r.id )? global.find(ro => String(ro.id) === r.id ).integrations:{},
        restr: restr,
        restrB: restrB,
        restrA: restrA,
        weekdays: weekdays
      }

    })

    roomsMapped.forEach(r => {
      roomsO[r.id] = r
    })

    return roomsO
  })


export interface IRoomWithEvents extends IRoom {
  events: IEvent[]
}

export interface IRoomWithCells extends IRoom {
  cells: Map<number, IEvent>
}

function formatNumber(number) {
  // Przycinanie do maksymalnie dwóch miejsc po przecinku
  let formatted: any = Math.round(number * 100) / 100;

  // Sprawdzenie, czy liczba ma część dziesiętną różną od zera
  if (formatted % 1 !== 0) {
    // Jeśli tak, zamień na string i usuń niepotrzebne zera
    formatted = formatted.toString().replace(/0+$/, '');
  } else {
    // Jeśli nie, zaokrąglamy do liczby całkowitej
    formatted = Math.round(formatted);
  }

  return formatted;
}
